// What a caller SENDS to trade (BACKLOG E6, screens/TRADES.md): an offer, an
// answer to one, and the trade block.
//
// Views live in `views.ts`; these are requests, the split `transactions.ts`
// makes. THE TEAM IS NOT IN ANY BODY: the path names the league and the
// session names the manager, so a body cannot speak for somebody else's team.

import { z } from 'zod';

const PlayerId = z.string().min(1).max(64);
const TeamId = z.string().min(1).max(128);

/** More than any roster holds, so the cap only ever refuses a malformed body. */
const MAX_IDS = 40;

const noRepeats = (ids: readonly string[]) => new Set(ids).size === ids.length;
const Ids = z.array(PlayerId).max(MAX_IDS).refine(noRepeats, 'Each player can be named once.');
/** Draft pick ids (`pickId`, PLAN-2026-10-27-the-next-season §2.6). */
const PickIds = z
  .array(z.string().min(1).max(200))
  .max(MAX_IDS)
  .refine(noRepeats, 'Each pick can be named once.');

/** TRADES.md: "Note (optional, 300 characters)". */
export const TRADE_NOTE_MAX = 300;

/**
 * `POST /leagues/:id/trades` — an offer to one other team, or a counter to an
 * offer made to me (`counterOf`), which closes that offer as `countered`.
 *
 * Players and draft picks (PLAN-2026-10-27-the-next-season §2.6, since
 * 2026-10-28); budget is a P2 asset (TRADES.md "The rules"). Whether the
 * league allows the trade at all — the deadline, the playoffs, the per-side
 * limit, the picks it trades, both rosters legal with the drops — is the
 * engine's `proposalProblem`, answered as a refusal with its sentence.
 */
export const TradeProposalRequest = z
  .object({
    /** The other team. */
    to: TeamId,
    /** My players I send. */
    give: Ids,
    /** Their players I ask for. */
    get: Ids,
    /** My draft picks I send, and theirs I ask for. Added 2026-10-28. */
    givePicks: PickIds.default([]),
    getPicks: PickIds.default([]),
    /** My players I drop so my roster is legal after. Empty when it already is. */
    myDrops: Ids.default([]),
    note: z.string().max(TRADE_NOTE_MAX).default(''),
    /** The offer made to me that this answers, or null for a fresh offer. */
    counterOf: z.string().min(1).max(128).nullable().default(null),
  })
  .superRefine((body, ctx) => {
    if (
      body.give.length === 0 &&
      body.get.length === 0 &&
      body.givePicks.length === 0 &&
      body.getPicks.length === 0
    )
      ctx.addIssue({
        code: 'custom',
        path: ['give'],
        message: 'A trade has to move a player or a pick.',
      });
    if (body.myDrops.some((id) => body.give.includes(id)))
      ctx.addIssue({
        code: 'custom',
        path: ['myDrops'],
        message: 'A player being traded cannot also be dropped.',
      });
  });
export type TradeProposalRequest = z.infer<typeof TradeProposalRequest>;

/**
 * `POST /leagues/:id/trades/:tradeId/:action` for accept, decline, withdraw,
 * vote, approve and veto. Counter is a new offer, so it is the proposal body
 * with `counterOf`, not this.
 *
 * One body for all six, each reading what it needs: `drops` on accept (mine,
 * when my roster would be over after), `reason` on decline (optional) and on a
 * commissioner's veto (required, TRADES.md "Review and veto"), `veto` on a
 * league vote.
 */
export const TradeActionRequest = z.object({
  drops: Ids.default([]),
  reason: z.string().trim().max(TRADE_NOTE_MAX).nullable().default(null),
  veto: z.boolean().default(true),
});
export type TradeActionRequest = z.infer<typeof TradeActionRequest>;

/** The actions the path's last segment may name. `counter` is a proposal. */
export const TRADE_PATH_ACTIONS = [
  'accept',
  'decline',
  'withdraw',
  'vote',
  'approve',
  'veto',
] as const;
export type TradePathAction = (typeof TRADE_PATH_ACTIONS)[number];

/**
 * `PUT /leagues/:id/teams/:teamId/trade-block` — my whole block as it should
 * stand: every player I am marking available, each with what I want back in my
 * own words. An empty list takes me off the block.
 */
export const TradeBlockRequest = z.object({
  entries: z
    .array(
      z.object({ playerId: PlayerId, wants: z.string().trim().max(TRADE_NOTE_MAX).default('') }),
    )
    .max(MAX_IDS)
    .refine((es) => noRepeats(es.map((e) => e.playerId)), 'Each player can be listed once.'),
});
export type TradeBlockRequest = z.infer<typeof TradeBlockRequest>;

/**
 * `GET /leagues/:id/trades/builder?with=&give=&get=&givePicks=&getPicks=&drops=&counterOf=`
 * — the builder as it stands with these boxes checked, recomputed as they
 * change. Lists are comma-separated ids (`give=p-1,p-2`); an empty or absent
 * parameter is none. `with` absent is the partner picker before a team is
 * chosen. The server keeps only what belongs where the query says: a player
 * not on my roster is not in `give`, nor a pick I do not hold in `givePicks`.
 */
export const TradeBuilderQuery = z.object({
  with: TeamId.nullable().default(null),
  give: Ids.default([]),
  get: Ids.default([]),
  givePicks: PickIds.default([]),
  getPicks: PickIds.default([]),
  drops: Ids.default([]),
  counterOf: z.string().min(1).max(128).nullable().default(null),
});
export type TradeBuilderQuery = z.infer<typeof TradeBuilderQuery>;

/** A comma list from a query parameter: absent or empty is none. */
export function idList(raw: string | null): string[] {
  return raw === null || raw === '' ? [] : raw.split(',').filter((s) => s !== '');
}
